/* eslint-disable complexity */
import React, { MouseEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { deburr, kebabCase } from 'lodash';

import { Checkbox } from 'carbon-react/lib/components/checkbox';
import Icon from 'carbon-react/lib/components/icon';
import Tooltip from 'carbon-react/lib/components/tooltip';

import { DataField } from '../../../types';
import { staticFormulaRefs } from '../../../constants';
import { getConsolidationShortCode } from '../../../utils-data';
import { useConsolidatedForecastingContext } from '../../../../forecast/detail/context/consolidated-forecasting-context';

import styles from './data-option.scss';

interface Props {
  option: DataField;
  type?: 'financial' | 'formula';
  selectedFields: DataField[];
  onSelectOption?: (ref: string) => void;
  onUnselectOption: (ref: string) => void;
  openFormulaDialog?: (id?: string) => void;
  disabledMsg?: ReactNode;
  overrideName?: string;
  description?: string;
  defaultExpandedState?: boolean;
  hideCheckbox?: boolean;
  showDeleteIcon?: boolean;
  hideChildren?: boolean;
  isParent?: boolean;
  highlightField?: (ref: string) => void;
  highlightedRef?: string;
  selectedParentRef: string;
  excludedChildren?: string[];
  includedChildren?: string[];
  addToExcludedField?: (parentRef: string, ref: string) => void;
  addToIncludedField?: (parentRef: string, ref: string) => void;
  parentExcluded?: boolean;
}

export const DataOption = ({
  option,
  type = 'financial',
  selectedFields,
  onSelectOption,
  onUnselectOption,
  openFormulaDialog,
  disabledMsg,
  overrideName,
  description,
  defaultExpandedState,
  hideCheckbox,
  showDeleteIcon,
  hideChildren,
  isParent,
  highlightField,
  highlightedRef,
  selectedParentRef,
  excludedChildren = [],
  includedChildren = [],
  addToExcludedField,
  addToIncludedField,
  parentExcluded = false
}: Props) => {
  const intl = useIntl();
  const { consolidation } = useConsolidatedForecastingContext();

  const [expanded, setExpanded] = useState<boolean>(!!defaultExpandedState);
  const [isSelected, setIsSelected] = useState<boolean>(false);
  const [isExcluded, setIsExcluded] = useState<boolean>(false);

  const hasChildren = useMemo(() => option.classifier !== 'account' && !!option.children?.length, [option]);

  useEffect(() => {
    setExpanded(!!defaultExpandedState);
  }, [defaultExpandedState]);

  useEffect(() => {
    setIsSelected(!!selectedFields.find(e => e.ref === option.ref));
  }, [selectedFields, option]);

  useEffect(() => {
    const isDirectlyExcluded = excludedChildren.includes(option.ref);
    const isDirectlyIncluded = includedChildren.includes(option.ref);
    setIsExcluded(isDirectlyExcluded || (parentExcluded && !isDirectlyIncluded));
  }, [excludedChildren, includedChildren, parentExcluded, option]);

  const handleEditClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    openFormulaDialog?.(option.ref);
  };

  const showEditIcon = type === 'formula' && !hasChildren;
  const notSelectable = (type === 'formula' && hasChildren) || !!option.not_selectable;

  const onToggleExpand = () => {
    if (hasChildren) {
      setExpanded(!expanded);
    }
    if (hideCheckbox && showDeleteIcon && highlightField) {
      highlightField(option.ref);
    }
  };

  const onToggleSelected = () => {
    if (isSelected) {
      onUnselectOption(option.ref);
    } else {
      onSelectOption?.(option.ref);
    }
  };

  const handleUnselect = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isSelected) {
      onUnselectOption(option.ref);
      setIsSelected(false);
    } else {
      addToExcludedField?.(selectedParentRef, option.ref);
    }
  };

  const handleRestoreExcluded = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isExcluded) {
      addToIncludedField?.(selectedParentRef, option.ref);
    }
  };

  const renderOption = () => (
    <span
      id={`option${hideCheckbox && showDeleteIcon ? '-selected' : ''}-${option.ref}`}
      className={clsx(
        styles.option,
        styles[type],
        styles[option.classifier || 'parent'],
        staticFormulaRefs.includes(option.ref) && styles.staticFormula,
        disabledMsg && !isSelected && styles.disabled,
        hasChildren && (!hideChildren || isParent) && styles.expandable,
        expanded && styles.expanded,
        highlightedRef === option.ref && styles.highlightAnimation,
        isExcluded && styles.excluded
      )}
      onClick={onToggleExpand}
      data-option-name={kebabCase(deburr(overrideName || option.name))}
      data-option-type={type}
    >
      {!hideCheckbox && !notSelectable && (
        <Checkbox
          className={clsx(styles.checkbox, isSelected && styles.checked)}
          size="small"
          checked={isSelected}
          onChange={onToggleSelected}
          disabled={!!disabledMsg}
          onClick={e => e.stopPropagation()}
          aria-labelledby={`${option.ref}-desc`}
        />
      )}
      <span className={styles.optionText} id={`${option.ref}-desc`}>
        {overrideName || option.name}
        {consolidation && option.organisation_id && (
          <span className={styles.orgCode}>{getConsolidationShortCode(consolidation, option.organisation_id)}</span>
        )}
        {description && <span className={styles.description}> {description}</span>}
        {showEditIcon &&
          !showDeleteIcon &&
          (option.locked ? (
            <button
              className={styles.iconButton}
              onClick={handleEditClick}
              title={intl.formatMessage({ id: 'reporting.view_formula', defaultMessage: 'View formula' })}
            >
              <Icon type="view" className={styles.carbonIcon} />
            </button>
          ) : (
            <button
              className={styles.iconButton}
              onClick={handleEditClick}
              title={intl.formatMessage({ id: 'reporting.edit_formula', defaultMessage: 'Edit formula' })}
            >
              <Icon type="edit" className={styles.carbonIcon} />
            </button>
          ))}
        {showDeleteIcon &&
          !isExcluded &&
          !notSelectable &&
          (selectedParentRef === option.ref ? (
            <button
              className={clsx(styles.iconButton, styles.right)}
              onClick={handleUnselect}
              title={intl.formatMessage({ id: 'reporting.deselect', defaultMessage: 'Deselect' })}
            >
              <Icon type="cross" />
            </button>
          ) : (
            <button
              className={clsx(styles.iconButton, styles.right)}
              onClick={handleUnselect}
              title={intl.formatMessage({ id: 'reporting.deselect', defaultMessage: 'Deselect' })}
            >
              <Icon type="minus" bgShape="circle" className={styles.circleIconCarbon} />
            </button>
          ))}
        {showDeleteIcon && isExcluded && !notSelectable && (
          <button
            className={clsx(styles.iconButton, styles.right)}
            onClick={handleRestoreExcluded}
            title={intl.formatMessage({ id: 'reporting.restore_excluded', defaultMessage: 'Restore excluded' })}
          >
            <Icon type="plus" bgShape="circle" className={styles.circleIconCarbon} />
          </button>
        )}
      </span>
    </span>
  );

  return (
    <li>
      <Tooltip message={disabledMsg} isVisible={!!disabledMsg && !isSelected}>
        {renderOption()}
      </Tooltip>

      {expanded && hasChildren && (!hideChildren || isParent) && (
        <ul className={clsx([styles.children])}>
          {(option.children || [])
            .filter(e => !e.disabled)
            .map(opt => {
              return (
                <DataOption
                  key={opt.ref}
                  option={opt}
                  type={type}
                  selectedFields={selectedFields}
                  onSelectOption={onSelectOption}
                  onUnselectOption={onUnselectOption}
                  disabledMsg={disabledMsg}
                  openFormulaDialog={openFormulaDialog}
                  defaultExpandedState={defaultExpandedState}
                  hideCheckbox={hideCheckbox}
                  hideChildren={hideChildren}
                  highlightedRef={highlightedRef}
                  showDeleteIcon={showDeleteIcon}
                  selectedParentRef={selectedParentRef}
                  addToExcludedField={addToExcludedField}
                  addToIncludedField={addToIncludedField}
                  excludedChildren={excludedChildren}
                  includedChildren={includedChildren}
                  parentExcluded={isExcluded}
                />
              );
            })}
        </ul>
      )}
    </li>
  );
};
